/* collision.js — collision primitives.
 * Ported from lhc-app.html: _aabbOverlap (~31806), _layerCollides (~32186),
 * _cabWallOverlap (~31877, oriented SAT), lhcVOverlap (~33009).
 */
import { RULE_EPS, WALL_EPS, LAYER_BANDS, WALL_THICKNESS } from "./constants";
import { placementAABB, placementOBB, cabinetWorldPos, lhcVSpan } from "./placement";
import { getWallGraph } from "./wallGraph";
import { sidePanelExtents } from "./sidePanelClearance";

/** 3D AABB overlap with 2 mm grace on all three axes. (~31806) */
export function aabbOverlap(a, b) {
  return (
    a.minX < b.maxX - RULE_EPS && a.maxX > b.minX + RULE_EPS &&
    a.minY < b.maxY - RULE_EPS && a.maxY > b.minY + RULE_EPS &&
    a.minZ < b.maxZ - RULE_EPS && a.maxZ > b.minZ + RULE_EPS
  );
}

/** Two roles collide iff their vertical bands overlap. (~32186) */
export function layerCollides(roleA, roleB) {
  return ((LAYER_BANDS[roleA] || 0) & (LAYER_BANDS[roleB] || 0)) !== 0;
}

/** True iff two placements overlap vertically (1 mm tolerance — butt-joints don't block). (~33009) */
export function lhcVOverlap(a, b, lib) {
  const A = lhcVSpan(a, lib[a.type]), B = lhcVSpan(b, lib[b.type]);
  if (!A || !B) return true;
  return A[0] < B[1] - 1e-3 && A[1] > B[0] + 1e-3;
}

/** obbOverlap(A, B, eps): exact oriented-footprint overlap (separating-axis test on the
 *  four edge normals) with the same `eps` penetration grace as aabbOverlap and the same
 *  vertical rule. A and B come from placementOBB. Identical to the AABB answer on 0/90°
 *  walls; exact on every other angle (batch 7, 2026-09-28). */
export function obbOverlap(A, B, eps = RULE_EPS) {
  if (!A || !B) return false;
  if (!(A.minY < B.maxY - eps && A.maxY > B.minY + eps)) return false;
  const dx = B.cx - A.cx, dz = B.cz - A.cz;
  const axes = [[A.ux, A.uz], [A.vx, A.vz], [B.ux, B.uz], [B.vx, B.vz]];
  for (const [nx, nz] of axes) {
    const hA = Math.abs(A.ux * nx + A.uz * nz) * A.hw + Math.abs(A.vx * nx + A.vz * nz) * A.hd;
    const hB = Math.abs(B.ux * nx + B.uz * nz) * B.hw + Math.abs(B.vx * nx + B.vz * nz) * B.hd;
    const dist = Math.abs(dx * nx + dz * nz);
    if (dist >= hA + hB - eps) return false; // a separating axis → no overlap
  }
  return true;
}

export function firstCollision(candidate, placements, lib, walls) {
  const cdef = lib[candidate.type];
  if (!cdef) return null;
  const cBox = placementAABB(candidate, cdef, walls);
  const cObb = placementOBB(candidate, cdef, walls);
  if (!cBox || !cObb) return null;
  for (const other of placements) {
    if (other.id === candidate.id) continue;
    const odef = lib[other.type];
    if (!odef) continue;
    const oBox = placementAABB(other, odef, walls);
    if (!oBox) continue;
    // AABB = cheap reject, OBB = the verdict (no false "collision" on angled walls)
    if (aabbOverlap(cBox, oBox) && obbOverlap(cObb, placementOBB(other, odef, walls))) return { other, oBox, cBox };
  }
  return null;
}

/** _cabWallOverlap(candidate, lib, walls): oriented SAT cabinet-OBB vs each wall-band-OBB.
 *  Returns the blocking wall number (index+1) or 0. Exact on diagonal walls. (~31877)
 *  5 mm penetration grace; connected-corner allowance WALL_THICKNESS/2 + EPS. */
export function cabWallOverlap(candidate, lib, walls) {
  const def = lib[candidate.type];
  if (!def) return 0;
  const pos = cabinetWorldPos(candidate, def, walls);
  if (!pos) return 0;
  const EPS = WALL_EPS;
  const ca = Math.cos(pos.angle), sa = Math.sin(pos.angle);
  const ux = ca, uz = -sa; // width axis
  const vx = sa, vz = ca; // depth axis
  /* a side panel reaches past the carcass — the wall SAT must see it, or the
   * board slides straight through the wall (2026-09-07 user report) */
  const _spE = sidePanelExtents(candidate, def);
  const _spS = (_spE.right - _spE.left) / 2;
  const _cx = pos.cx + ux * _spS, _cz = pos.cz + uz * _spS;
  const hw = def.width / 2 + (_spE.left + _spE.right) / 2, hd = def.depth / 2;
  const cCorners = [
    [_cx + ux * hw + vx * hd, _cz + uz * hw + vz * hd],
    [_cx - ux * hw + vx * hd, _cz - uz * hw + vz * hd],
    [_cx + ux * hw - vx * hd, _cz + uz * hw - vz * hd],
    [_cx - ux * hw - vx * hd, _cz - uz * hw - vz * hd],
  ];
  const WT = WALL_THICKNESS;
  // v0.50 CORNER-TOL FIX (BUG A, lhc-app.html ~32065), v0.52 -> 2026-09-28: the
  // corner neighbours come from the wall graph's NODE MEMBERSHIP (the same source
  // wallFaceSpan uses — never a re-derived endpoint distance), so a wall the
  // inner-span treats as a corner also gets the WT/2 corner allowance here —
  // otherwise a flush-at-inner-corner cabinet in an irregular room is refused
  // mid-drag. Identical to the old jointsCoincide pairing for coincident joints,
  // and it also covers legs the graph captured by their face vertex.
  const _cwInfo = candidate.wallId ? getWallGraph(walls).walls.get(candidate.wallId) : null;
  const _connectedIds = new Set(_cwInfo
    ? [..._cwInfo.neighborsAtStart, ..._cwInfo.neighborsAtEnd].map((nb) => nb.wallId)
    : []);
  // the host itself keeps the allowance (legacy: its endpoints matched themselves) — the
  // SAT band is the GLOBAL thickness, so a thinner host's real face sits inside it
  if (candidate.wallId) _connectedIds.add(candidate.wallId);
  for (let wi = 0; wi < walls.length; wi++) {
    const w = walls[wi];
    if ((w.style || "standard") === "peninsula") continue;
    const _connected = _connectedIds.has(w.id);
    const _thresh = _connected ? WT / 2 + EPS : EPS;
    const ws = w.start, we = w.end;
    const dx = we[0] - ws[0], dz = we[1] - ws[1];
    const wlen = Math.hypot(dx, dz);
    if (wlen < 0.01) continue;
    const ax = dx / wlen, az = dz / wlen, nxw = -az, nzw = ax;
    const wmx = (ws[0] + we[0]) / 2, wmz = (ws[1] + we[1]) / 2;
    const wCorners = [
      [wmx + ax * wlen / 2 + nxw * WT / 2, wmz + az * wlen / 2 + nzw * WT / 2],
      [wmx - ax * wlen / 2 + nxw * WT / 2, wmz - az * wlen / 2 + nzw * WT / 2],
      [wmx + ax * wlen / 2 - nxw * WT / 2, wmz + az * wlen / 2 - nzw * WT / 2],
      [wmx - ax * wlen / 2 - nxw * WT / 2, wmz - az * wlen / 2 - nzw * WT / 2],
    ];
    const axes = [[ux, uz], [vx, vz], [ax, az], [nxw, nzw]];
    let minOverlap = Infinity;
    for (const [axx, axz] of axes) {
      let c1 = Infinity, c2 = -Infinity, w1 = Infinity, w2 = -Infinity;
      for (const [px, pz] of cCorners) { const t = px * axx + pz * axz; if (t < c1) c1 = t; if (t > c2) c2 = t; }
      for (const [px, pz] of wCorners) { const t = px * axx + pz * axz; if (t < w1) w1 = t; if (t > w2) w2 = t; }
      const ov = Math.min(c2, w2) - Math.max(c1, w1);
      if (ov < minOverlap) minOverlap = ov;
      if (minOverlap <= EPS) break;
    }
    if (minOverlap > _thresh) return wi + 1;
  }
  return 0;
}
